import {
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Boxes,
  ShieldCheck,
  Building2,
  CircleDollarSign,
  ClipboardList,
  Factory,
  FileWarning,
  FlaskConical,
  Image,
  Mail,
  PackageOpen,
  QrCode,
  ShieldQuestion,
  Sparkles,
  Truck,
  Undo2,
  Warehouse,
  Circle,
  FileCheck2,
  FileSignature,
  FileText,
  HandCoins,
  Inbox,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  Layers,
  Package,
  Radio,
  Megaphone,
  Plug,
  ReceiptText,
  Repeat,
  Tags,
  UserCog,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { SidebarNavGroup } from '../components/shell/SharedSidebarShell';

/**
 * Unified workspace nav (ADR-040). The runtime (`/auth/me` → `workspace.nav`) serves a
 * cross-app sidebar for single-workspace verticals (today: SOFTWARE) as nav-as-data:
 * each item names its owning `app` + in-app `path` + an `iconKey`. This module resolves
 * that data into a `SidebarNavGroup[]` the SharedSidebarShell renders identically in
 * every app — so Intent/Kosh/Catalog feel like ONE workspace and no app-switcher is
 * needed. Items owned by the CURRENT app client-route (active-highlighted); items owned
 * by another app render as same-tab full-load links (`crossApp`) resolved against that
 * app's base URL from the workspace registry.
 */

/** The FE shape of a runtime `workspace.nav` item (gating stripped server-side). */
export interface WorkspaceNavItemDef {
  id: string;
  label: string;
  iconKey: string;
  /** Owning workspace app id (matches a `workspace.apps` entry). */
  app: string;
  /** In-app path WITHOUT a leading slash. */
  path: string;
}

export interface WorkspaceNavGroupDef {
  id: string;
  label?: string;
  items: WorkspaceNavItemDef[];
}

/** Minimal app shape the resolver needs (a subset of `workspace.apps`). */
export interface WorkspaceNavApp {
  id: string;
  /** Base URL (same-origin `/kosh/` in a workspace build, or an absolute subdomain). */
  url: string;
}

// iconKey → lucide icon for the unified nav sections. Unknown keys fall back to a dot.
const WORKSPACE_NAV_ICON: Record<string, LucideIcon> = {
  dashboard: LayoutDashboard,
  leads: UserPlus,
  quotes: FileText,
  proposals: FileSignature,
  contracts: FileCheck2,
  subscriptions: Repeat,
  customers: Users,
  invoices: ReceiptText,
  receivables: Landmark,
  collections: HandCoins,
  products: Package,
  plans: Layers,
  offerings: Tags,
  sources: Radio,
  capture: Inbox,
  assignment: UserCog,
  pricebook: BookOpen,
  reminders: Bell,
  organization: Building2,
  // Operations / COMMERCE destinations (the catalog covers the whole Operations
  // sidebar now, not just the software-relevant slice). An unmapped key renders a
  // dot, so every catalog iconKey must exist here.
  guide: BookOpen,
  inventory: Boxes,
  categories: Tags,
  merchandising: Sparkles,
  packaging: PackageOpen,
  'finished-goods': Package,
  barcodes: QrCode,
  vendors: Building2,
  'raw-materials': Layers,
  'purchase-requests': ClipboardList,
  recipes: Factory,
  'job-work': Boxes,
  'purchase-orders': PackageOpen,
  dispatch: Truck,
  'cancel-requests': FileWarning,
  returns: Undo2,
  refunds: CircleDollarSign,
  reviews: ShieldQuestion,
  reports: BarChart3,
  warehouses: Warehouse,
  email: Mail,
  'test-data': FlaskConical,
  banner: Image,
  // Pre-existing Engage keys that were never mapped — they had been rendering dots.
  support: LifeBuoy,
  chatbot: Bot,
  security: ShieldCheck,
  campaigns: Megaphone,
  integrations: Plug,
};

/** Join an app base URL with an in-app path → one clean, single-slashed href. */
function joinHref(base: string, path: string): string {
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/**
 * Resolve the runtime `workspace.nav` into renderable SharedSidebarShell groups.
 *
 * @param nav          the `workspace.nav` groups from `/auth/me`
 * @param currentAppId the app this shell belongs to (e.g. 'intent', 'kosh')
 * @param apps         the `workspace.apps` registry (for cross-app base URLs)
 */
export function resolveWorkspaceNav(
  nav: WorkspaceNavGroupDef[],
  currentAppId: string,
  apps: WorkspaceNavApp[] | null | undefined,
): SidebarNavGroup[] {
  const baseById = new Map((apps ?? []).map((a) => [a.id, a.url] as const));
  return nav.map((group) => ({
    id: group.id,
    label: group.label,
    items: group.items.map((item) => {
      const icon = WORKSPACE_NAV_ICON[item.iconKey] ?? Circle;
      if (item.app === currentAppId) {
        // Owned by this app → client-route (active-highlighted) relative to its basename.
        return { id: item.id, label: item.label, icon, href: `/${item.path.replace(/^\/+/, '')}` };
      }
      const base = baseById.get(item.app) ?? `/${item.app}/`;
      return {
        id: item.id,
        label: item.label,
        icon,
        href: joinHref(base, item.path),
        crossApp: true,
      };
    }),
  }));
}
